import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Calendar, Clock, MapPin, X, CheckCircle2, ArrowRight, Star } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Card';
import { formatINR, formatDate, slotLabel } from '@/lib/utils';
import { useToast } from '@/components/ui/Toast';
import { usePayment } from '@/lib/payment';
import { useAuth } from '@/lib/auth';
import { useVenue } from '@/lib/venue';
import { ReviewModal } from '@/components/ReviewModal';

// Mirrors app.services.refund_amount_for_booking on the backend — used
// only to preview the refund % in the cancel confirmation; the backend is
// the actual source of truth for what gets refunded. booking_date/start_hour
// are wall-clock values (not UTC), so this deliberately uses the browser's
// LOCAL time rather than anchoring to UTC or hardcoding a timezone offset.
function previewRefundPercent(b, tiers) {
  const slotStart = new Date(`${b.booking_date}T00:00:00`);
  slotStart.setHours(slotStart.getHours() + b.start_hour);
  const noticeHours = (slotStart.getTime() - Date.now()) / 3600000;
  const applicable = tiers.filter((t) => noticeHours >= t.hours_before);
  if (applicable.length === 0) return 0;
  return applicable.reduce((best, t) => (t.hours_before > best.hours_before ? t : best)).refund_percent;
}

export default function MyBookings() {
  const [bookings, setBookings] = useState([]);
  const [tab, setTab] = useState('upcoming');
  const [loading, setLoading] = useState(true);
  const [reviewTarget, setReviewTarget] = useState(null); // { academyId, venueName } | null
  const [refundTiers, setRefundTiers] = useState([]);
  const [featuredOffers, setFeaturedOffers] = useState([]);
  const [feed, setFeed] = useState([]);
  const { toast } = useToast();
  const pay = usePayment();
  const navigate = useNavigate();
  const { venues } = useVenue();
  const venueName = (academyId) => venues.find((v) => v.id === academyId)?.name || 'this venue';

  useEffect(() => {
    api.get('/config').then(({ data }) => setRefundTiers(data.refund_tiers || [])).catch(() => {});
    // This page isn't scoped to one venue, so only platform-wide featured
    // offers show here — a venue-specific featured offer surfaces once the
    // customer picks that venue, in BookCourt.jsx/Memberships.jsx.
    api.get('/offers', { params: { featured_only: true } }).then(({ data }) => setFeaturedOffers(data || [])).catch(() => {});
    // Super_admin-published feed — ads/offer promos/facility announcements,
    // same content shown on the mobile home tab. See app.routers.feed.
    api.get('/feed').then(({ data }) => setFeed(data || [])).catch(() => {});
  }, []);

  const handleNewBooking = () => {

  if (!user?.phone) {

    navigate('/app/complete-profile', {
      state: {
        redirectTo: '/app/book',
      },
    });

    return;
  }

  navigate('/app/book');
};

const { user } = useAuth();

  const load = async () => {
    try {
      const { data } = await api.get('/bookings/mine');
      setBookings(data);
    } catch (e) {
      toast({ title: 'Failed to load bookings', variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const todayStr = new Date().toISOString().slice(0, 10);
  const filtered = bookings.filter((b) => {
    if (tab === 'upcoming') return b.booking_date >= todayStr && b.status !== 'cancelled';
    if (tab === 'past') return b.booking_date < todayStr || b.status === 'cancelled';
    if (tab === 'pending') return b.payment_status === 'pending' && b.status !== 'cancelled';
    return true;
  });

  const cancel = async (b) => {
    const refundNote = b.payment_status !== 'paid'
      ? "This booking hasn't been paid, so there's nothing to refund."
      : (() => {
          const pct = previewRefundPercent(b, refundTiers);
          return pct > 0
            ? `You'll get ${pct}% of ${formatINR(b.amount)} back based on how much notice you're giving.`
            : "Based on how much notice you're giving, this cancellation won't be refunded.";
        })();
    if (!window.confirm(`Cancel this booking?\n\n${refundNote}`)) return;
    try {
      const { data } = await api.post(`/bookings/${b.id}/cancel`);
      toast({
        title: 'Booking cancelled',
        description: data.refunded ? `${formatINR(data.refund_amount)} refunded (${data.refund_percent}%)` : 'No refund was due.',
        variant: 'success',
      });
      load();
    } catch (e) {
      toast({ title: 'Cancel failed', description: e.response?.data?.detail || '', variant: 'error' });
    }
  };

  return (
    <div className="space-y-8" data-testid="my-bookings-page">
      {feed.length > 0 && (
        <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1" data-testid="home-feed">
          {feed.map((f) => {
            const card = (
              <div className="min-w-[260px] max-w-[260px] bg-surface border border-white/10 flex flex-col">
                {f.image_url && (
                  <div className="aspect-video bg-obsidian overflow-hidden">
                    <img src={f.image_url} alt={f.title} className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="p-4 space-y-1">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-volt">{f.type}</div>
                  <div className="font-heading font-bold uppercase text-sm truncate">{f.title}</div>
                  {f.description && <p className="text-xs text-white/50 line-clamp-2">{f.description}</p>}
                </div>
              </div>
            );
            if (!f.link_url) return <div key={f.id}>{card}</div>;
            return /^https?:\/\//.test(f.link_url) ? (
              <a key={f.id} href={f.link_url} target="_blank" rel="noreferrer" className="hover:opacity-90 transition-opacity">{card}</a>
            ) : (
              <Link key={f.id} to={f.link_url} className="hover:opacity-90 transition-opacity">{card}</Link>
            );
          })}
        </div>
      )}
      {featuredOffers.length > 0 && (
        <div className="bg-volt/10 border border-volt p-5 sm:p-6 space-y-2" data-testid="featured-offers-banner">
          <Badge tone="volt">🎉 Offer{featuredOffers.length > 1 ? 's' : ''}</Badge>
          {featuredOffers.map((o) => (
            <div key={o.id}>
              <div className="font-heading font-bold text-lg uppercase">
                {o.title} — {o.discount_type === 'percent' ? `${o.discount_value}% off` : `${formatINR(o.discount_value)} off`}
              </div>
              {o.description && <p className="text-sm text-white/60">{o.description}</p>}
            </div>
          ))}
        </div>
      )}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-volt mb-2">Your Court Life</div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl uppercase tracking-tight">My Bookings</h1>
        </div>
        <Link to="/app/book" data-testid="new-booking-cta">
          <Button
  onClick={handleNewBooking}
  data-testid="new-booking-cta"
>
  New Booking
  <ArrowRight size={16} />
</Button>
        </Link>
      </div>

      <div className="flex gap-1 border-b border-white/10">
        {[
          ['upcoming', 'Upcoming'],
          ['pending', 'Awaiting Payment'],
          ['past', 'Past / Cancelled'],
        ].map(([k, l]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`tab-${k}`}
            className={`px-4 py-3 text-[11px] font-bold uppercase tracking-[0.2em] border-b-2 transition-colors ${
              tab === k ? 'border-volt text-volt' : 'border-transparent text-white/40 hover:text-white'
            }`}
          >{l}</button>
        ))}
      </div>

      {loading ? (
        <div className="text-white/40 text-sm">Loading…</div>
      ) : filtered.length === 0 ? (
        <EmptyState
  tab={tab}
  handleNewBooking={handleNewBooking}
/>
      ) : (
        <div className="space-y-3">
          {filtered.map((b) => (
            <BookingCard
              key={b.id}
              b={b}
              venue={venues.find((v) => v.id === b.academy_id)}
              onCancel={cancel}
              onPay={() => pay({ purpose: 'booking', reference_id: b.id, onSuccess: load })}
              onRate={() => setReviewTarget({ academyId: b.academy_id, venueName: venueName(b.academy_id) })}
            />
          ))}
        </div>
      )}

      <ReviewModal
        open={!!reviewTarget}
        academyId={reviewTarget?.academyId}
        venueName={reviewTarget?.venueName}
        onClose={() => setReviewTarget(null)}
      />
    </div>
  );
}

// A slot that's already started/passed can't be "un-booked" — matches the
// backend's own guard in app.routers.bookings.cancel_booking. Local
// wall-clock time, same convention as previewRefundPercent above.
function hasPassed(b) {
  const slotStart = new Date(`${b.booking_date}T00:00:00`);
  slotStart.setHours(slotStart.getHours() + b.start_hour);
  return slotStart.getTime() <= Date.now();
}

function BookingCard({ b, venue, onCancel, onPay, onRate }) {
  const isPaid = b.payment_status === 'paid';
  const past = hasPassed(b);
  const location = venue
    ? [venue.name, venue.address].filter(Boolean).join(', ')
    : 'Venue unavailable';
  return (
    <div className="bg-surface border border-white/10 p-5 sm:p-6 flex flex-col sm:flex-row gap-6 sm:items-center" data-testid={`booking-row-${b.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-3">
          <Badge tone={b.status === 'cancelled' ? 'danger' : isPaid ? 'success' : 'warning'}>
            {b.status === 'cancelled' ? 'Cancelled' : isPaid ? 'Confirmed' : 'Awaiting Payment'}
          </Badge>
          <Badge>Booking ID · {b.id.slice(0, 8)}</Badge>
        </div>
        <h3 className="font-heading font-bold text-2xl uppercase tracking-tight mb-3">{b.court_name}</h3>
        <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/60">
          <div className="flex items-center gap-2"><Calendar size={14} className="text-volt" /> {formatDate(b.booking_date)}</div>
          <div className="flex items-center gap-2"><Clock size={14} className="text-volt" /> {slotLabel(b.start_hour, b.duration_hours)}</div>
          <div className="flex items-center gap-2"><MapPin size={14} className="text-volt" /> {location}</div>
        </div>
      </div>
      <div className="flex flex-col items-start sm:items-end gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">Amount</div>
          <div className="font-heading font-black text-3xl text-volt">{formatINR(b.amount)}</div>
          {b.discount > 0 && (
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">Saved {formatINR(b.discount)} (member)</div>
          )}
        </div>
        {/*<div className="flex gap-2">
          {!isPaid && b.status !== 'cancelled' && (
            <Button size="sm" onClick={onPay} data-testid={`pay-booking-${b.id}`}><CreditCard size={14} /> Pay Now</Button>
          )}*/}
        <div className="flex flex-col items-start sm:items-end gap-2">
          {isPaid && b.status !== 'cancelled' && (
            <Button variant="outline" size="sm" onClick={onRate} data-testid={`rate-booking-${b.id}`}>
              <Star size={14} /> Rate Venue
            </Button>
          )}
          {b.status !== 'cancelled' && !past && (
            <Button variant="danger" size="sm" onClick={() => onCancel(b)} data-testid={`cancel-booking-${b.id}`}>
              <X size={14} /> Cancel Booking
            </Button>
          )}
        </div>
        </div>
      </div>
  );
}

function EmptyState({
  tab,
  handleNewBooking,
}) {
  return (
    <div className="bg-surface border border-white/10 p-14 text-center">
      <CheckCircle2 size={36} className="text-volt mx-auto mb-4" />
      <h3 className="font-heading font-bold text-2xl uppercase tracking-tight mb-2">No {tab} bookings</h3>
      <p className="text-sm text-white/50 mb-6">Reserve a court — it takes less than a minute.</p>
      <Link to="/app/book"><Button onClick={handleNewBooking}>
  Book a Court
  <ArrowRight size={14} />
</Button></Link>
    </div>
  );
}
